import { useReducer, useCallback, useId } from 'react';
import {
  Box,
  Button,
  Checkbox,
  InlineStack,
  Panel,
  PanelActions,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Radio,
  Stack,
  Text,
  TextLabel,
  TextField,
} from '@customerio/pluma-components/react';
import type { CopilotToolCallStatus } from '@customerio/hydra-copilot';
import type { AskUserQuestionArgs } from './index';
import * as styles from './index.css';

interface Props {
  args: AskUserQuestionArgs;
  status: CopilotToolCallStatus;
  /** The answer text from a previous response (used to restore state on refresh). */
  answerText?: string;
  onAnswer: (answer: string) => void;
}

interface QuestionAnswer {
  selected: string[];
  otherSelected: boolean;
  otherValue: string;
}

interface State {
  currentIndex: number;
  questionAnswers: Record<number, QuestionAnswer>;
  submitted: boolean;
}

type Action =
  | { type: 'toggle_option'; index: number; label: string; multiSelect: boolean }
  | { type: 'toggle_other'; index: number; multiSelect: boolean }
  | { type: 'set_other_value'; index: number; value: string }
  | { type: 'go_to'; index: number }
  | { type: 'submit' };

const emptyAnswer: QuestionAnswer = { selected: [], otherSelected: false, otherValue: '' };

function getAnswer(state: State, index: number): QuestionAnswer {
  return state.questionAnswers[index] ?? emptyAnswer;
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'toggle_option': {
      const current = getAnswer(state, action.index);
      if (action.multiSelect) {
        const selected = current.selected.includes(action.label)
          ? current.selected.filter((l) => l !== action.label)
          : [...current.selected, action.label];
        return {
          ...state,
          questionAnswers: { ...state.questionAnswers, [action.index]: { ...current, selected } },
        };
      }
      // Single select — pick this option, clear "other"
      return {
        ...state,
        questionAnswers: {
          ...state.questionAnswers,
          [action.index]: { ...current, selected: [action.label], otherSelected: false },
        },
      };
    }
    case 'toggle_other': {
      const current = getAnswer(state, action.index);
      if (action.multiSelect) {
        return {
          ...state,
          questionAnswers: {
            ...state.questionAnswers,
            [action.index]: { ...current, otherSelected: !current.otherSelected },
          },
        };
      }
      // Single select — pick "other", clear selected options
      return {
        ...state,
        questionAnswers: {
          ...state.questionAnswers,
          [action.index]: { ...current, selected: [], otherSelected: true },
        },
      };
    }
    case 'set_other_value': {
      const current = getAnswer(state, action.index);
      return {
        ...state,
        questionAnswers: {
          ...state.questionAnswers,
          [action.index]: { ...current, otherValue: action.value },
        },
      };
    }
    case 'go_to':
      return { ...state, currentIndex: action.index };
    case 'submit':
      return { ...state, submitted: true };
  }
}

type AnswerPayload = Array<{
  question: string;
  answers: string[];
}>;

export function parseAnswerString(
  _questions: AskUserQuestionArgs['questions'],
  answerText: string,
): Record<number, QuestionAnswer> {
  const answers: Record<number, QuestionAnswer> = {};

  try {
    const payload = JSON.parse(answerText);
    for (let index = 0; index < payload.length; index++) {
      const entry = payload[index];
      if (entry.answers.length === 0) continue;

      const knownLabels = new Set(_questions[index]?.options.map((option) => option.label) ?? []);
      const selected: string[] = [];
      let otherValue = '';

      for (const answer of entry.answers) {
        if (knownLabels.has(answer)) {
          selected.push(answer);
        } else {
          otherValue = answer;
        }
      }

      answers[index] = {
        selected,
        otherSelected: otherValue.length > 0,
        otherValue,
      };
    }
  } catch {
    // Fall back gracefully if the format is unexpected
  }

  return answers;
}

export function buildAnswerString(
  questions: AskUserQuestionArgs['questions'],
  questionAnswers: Record<number, QuestionAnswer>,
): string {
  const payload: AnswerPayload = [];
  for (let index = 0; index < questions.length; index++) {
    const question = questions[index];
    const answer = questionAnswers[index] ?? emptyAnswer;
    const otherText = answer.otherSelected && answer.otherValue.trim() !== '' ? answer.otherValue.trim() : null;
    const allAnswers = [...answer.selected, ...(otherText != null ? [otherText] : [])];
    payload.push({ question: question.question, answers: allAnswers });
  }
  return JSON.stringify(payload);
}

export function AskUserQuestion({ args, status, answerText, onAnswer }: Props) {
  const { questions } = args;
  const [state, dispatch] = useReducer(reducer, undefined, () => {
    // Restore selections from a previous answer if available (e.g. after page refresh)
    const restoredAnswers = answerText != null && status === 'success' ? parseAnswerString(questions, answerText) : {};
    const isRestored = Object.keys(restoredAnswers).length > 0;
    return {
      currentIndex: isRestored ? questions.length - 1 : 0,
      questionAnswers: restoredAnswers,
      submitted: isRestored,
    };
  });
  const { currentIndex, questionAnswers, submitted } = state;

  const question = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const isFirstQuestion = currentIndex === 0;
  const answer = getAnswer(state, currentIndex);
  const hasSelection = answer.selected.length > 0 || (answer.otherSelected && answer.otherValue.trim() !== '');
  const isComplete = status === 'success' || submitted;

  const handleAdvance = useCallback(() => {
    if (isLastQuestion) {
      dispatch({ type: 'submit' });
      const answerString = buildAnswerString(questions, questionAnswers);
      onAnswer(answerString);
    } else {
      dispatch({ type: 'go_to', index: currentIndex + 1 });
    }
  }, [isLastQuestion, questions, questionAnswers, currentIndex, onAnswer]);

  if (question == null) return null;

  const selectionCount = answer.selected.length + (answer.otherSelected && answer.otherValue.trim() !== '' ? 1 : 0);

  return (
    <Panel>
      {/* Header */}
      <PanelHeader>
        <PanelTitle>
          <InlineStack alignBlock="center" alignInline="space-between" flexGrow="1">
            <Text size="md" weight="medium">
              {question.question}
            </Text>
            {questions.length > 1 ? (
              <InlineStack alignBlock="center" gap="050" flexShrink="0" wrap="nowrap">
                <Button
                  variant="tertiary"
                  size="sm"
                  icon="chevron-left"
                  isIconOnly={true}
                  aria-label="Previous question"
                  isDisabled={isFirstQuestion}
                  onClick={() => dispatch({ type: 'go_to', index: currentIndex - 1 })}
                />
                <Text size="xs" color="subtle">
                  {currentIndex + 1} of {questions.length}
                </Text>
                <Button
                  variant="tertiary"
                  size="sm"
                  icon="chevron-right"
                  isIconOnly={true}
                  aria-label="Next question"
                  isDisabled={isLastQuestion}
                  onClick={() => dispatch({ type: 'go_to', index: currentIndex + 1 })}
                />
              </InlineStack>
            ) : null}
          </InlineStack>
        </PanelTitle>
      </PanelHeader>
      {/* Options */}
      <PanelSection>
        <Stack gap="050">
          {question.options.map((option) => (
            <OptionRow
              key={option.label}
              label={option.label}
              description={option.description}
              isSelected={answer.selected.includes(option.label)}
              isMultiSelect={question.multiSelect}
              isDisabled={isComplete}
              onSelect={() =>
                dispatch({
                  type: 'toggle_option',
                  index: currentIndex,
                  label: option.label,
                  multiSelect: question.multiSelect,
                })
              }
            />
          ))}

          {/* Something else option */}
          <SomethingElseOption
            isSelected={answer.otherSelected}
            isMultiSelect={question.multiSelect}
            isDisabled={isComplete}
            onSelect={() => dispatch({ type: 'toggle_other', index: currentIndex, multiSelect: question.multiSelect })}
            otherValue={answer.otherValue}
            onOtherValueChange={(value) => dispatch({ type: 'set_other_value', index: currentIndex, value })}
          />
        </Stack>
      </PanelSection>
      {/* Footer */}
      <InlineStack alignBlock="center" alignInline="space-between">
        <Text size="xs" color="subtle">
          {isComplete ? 'Answered' : question.multiSelect ? `${selectionCount} selected` : null}
        </Text>
        {!isComplete ? (
          <PanelActions>
            <Button variant="tertiary" size="sm" onClick={handleAdvance}>
              Skip
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={isLastQuestion ? undefined : 'chevron-right'}
              isIconOnly={!isLastQuestion}
              aria-label={isLastQuestion ? undefined : 'Next'}
              isDisabled={!hasSelection}
              onClick={handleAdvance}
            >
              {isLastQuestion ? 'Done' : undefined}
            </Button>
          </PanelActions>
        ) : null}
      </InlineStack>
    </Panel>
  );
}

type OptionRowProps = {
  label: string;
  description: string;
  isSelected: boolean;
  isMultiSelect: boolean;
  isDisabled: boolean;
  onSelect: () => void;
};

function OptionRow({ label, description, isSelected, isMultiSelect, isDisabled, onSelect }: OptionRowProps) {
  const id = useId();
  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;

  return (
    <TextLabel
      htmlFor={id}
      py="075"
      px="100"
      color="inherit"
      textTransform="none"
      className={`${styles.optionRow} ${isSelected ? styles.optionRowSelected : ''} ${
        isDisabled ? styles.optionRowDisabled : ''
      }`}
    >
      <InlineStack alignBlock="start" gap="100">
        <Box pt="050" flexShrink="0">
          {isMultiSelect ? (
            <Checkbox
              id={id}
              ariaLabelledby={labelId}
              ariaDescribedby={descriptionId}
              isChecked={isSelected}
              isDisabled={isDisabled}
              onCheckedChange={onSelect}
            />
          ) : (
            <Radio
              id={id}
              ariaLabelledby={labelId}
              ariaDescribedby={descriptionId}
              isChecked={isSelected}
              isDisabled={isDisabled}
              onCheckedChange={onSelect}
            />
          )}
        </Box>
        <Stack gap="025">
          <Text id={labelId} size="sm" weight="medium" color="base">
            {label}
          </Text>
          <Text id={descriptionId} size="xs" color="subtle">
            {description}
          </Text>
        </Stack>
      </InlineStack>
    </TextLabel>
  );
}

type SomethingElseOptionProps = {
  isSelected: boolean;
  isMultiSelect: boolean;
  isDisabled: boolean;
  onSelect: () => void;
  otherValue: string;
  onOtherValueChange: (value: string) => void;
};

function SomethingElseOption({
  isSelected,
  isMultiSelect,
  isDisabled,
  onSelect,
  otherValue,
  onOtherValueChange,
}: SomethingElseOptionProps) {
  const id = useId();
  const labelId = `${id}-label`;

  return (
    <Stack
      py="075"
      px="100"
      gap="100"
      className={`${styles.optionRow} ${isSelected ? styles.optionRowSelected : ''} ${
        isDisabled ? styles.optionRowDisabled : ''
      }`}
    >
      <TextLabel htmlFor={id} textTransform="none" cursor="pointer" color="inherit">
        <InlineStack alignBlock="center" gap="100">
          {isMultiSelect ? (
            <Checkbox
              id={id}
              ariaLabelledby={labelId}
              isChecked={isSelected}
              isDisabled={isDisabled}
              onCheckedChange={onSelect}
            />
          ) : (
            <Radio
              id={id}
              ariaLabelledby={labelId}
              isChecked={isSelected}
              isDisabled={isDisabled}
              onCheckedChange={onSelect}
            />
          )}
          <Text id={labelId} size="sm" weight="medium" color="base">
            Something else
          </Text>
        </InlineStack>
      </TextLabel>
      {isSelected ? (
        <Box pl="300" onClick={(event) => event.stopPropagation()}>
          <TextField
            size="sm"
            value={otherValue}
            onInput={(event) => onOtherValueChange(event.target.value)}
            placeholder="Type your answer"
            isDisabled={isDisabled}
            ariaLabel="Custom answer"
          />
        </Box>
      ) : null}
    </Stack>
  );
}
